import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { fetchJSON } from 'api/fetch';
import { captureException } from 'fe-core/util/errorTracking';
import { Map } from 'immutable';
import { RootState } from 'initializers/types';

import { PARENT_SLICE_NAME, SLICE_NAME } from './constants';
import type { SessionSerializedEntity, SessionState } from './types';

const initialState: SessionState = {
  canManageEmployees: false,
  status: 'idle',
};

export const fetchSession = createAsyncThunk('session/fetch', async () => {
  try {
    const response = await fetchJSON(`/schedule_builder/session`);
    return response;
  } catch (error) {
    captureException(error);
  }
});

export const sliceState = (state: { [x: string]: any }) =>
  state.get(PARENT_SLICE_NAME)?.[SLICE_NAME];

export const selectSessionStatus = (state: RootState) =>
  state.get(PARENT_SLICE_NAME)?.[SLICE_NAME]?.status;

export const selectCanManageEmployees = (state: Map<string, any>): boolean =>
  sliceState(state)?.canManageEmployees;

export const selectColorMappingVersion = (
  state: Map<string, any>
): number | undefined => sliceState(state)?.colorMappingVersion;

export const selectDepartmentIdsToClear = (
  state: RootState
): string[] | undefined => {
  const isRestrictedToDepartments =
    sliceState(state)?.isRestrictedToDepartments;
  const assignedDepartmentsToUser = sliceState(state)?.departments;

  return isRestrictedToDepartments && assignedDepartmentsToUser
    ? assignedDepartmentsToUser
        .map((dept: any) => dept.id?.toString())
        .filter(Boolean)
    : undefined;
};

const slice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder.addCase(fetchSession.fulfilled, (state, action) => {
      if (action.payload?.data) {
        const session: SessionSerializedEntity = action?.payload?.data;
        if (session?.attributes) {
          state.canManageEmployees = session.attributes.canManageEmployees;
          state.colorMappingVersion = session.attributes.colorMappingVersion;
          state.isRestrictedToDepartments =
            session.attributes.isRestrictedToDepartments;
        }
        if (session?.relationships) {
          const flattenedRelationships = Object.fromEntries(
            Object.entries(session.relationships || {}).map(
              ([k, v]: [string, any]) => [k, v.data]
            )
          );
          state.currentLocation = flattenedRelationships?.currentLocation;
          state.currentUser = flattenedRelationships?.currentUser;
          state.departments = flattenedRelationships?.departments;
          state.templates = flattenedRelationships?.templates;
          state.locations = flattenedRelationships?.locations;
        }
      }
      state.status = 'succeeded';
    });
  },
});

export const reducer = slice.reducer;
